import { safeCallback } from "../helpers/fn.js";
import { isArray, isObject } from "../helpers/is-type.js";
import { createSubject } from "../helpers/subject.js";
import { addHmrObservable } from "../render/hmr.js";
import { cancelTask, effect, watchEffect } from "./scheduler.js";
//#region ../r-html/dist/observable/index.js
var rawToProxy = /* @__PURE__ */ new WeakMap();
var rawToObservers = /* @__PURE__ */ new WeakMap();
var proxyToRaw = /* @__PURE__ */ new WeakMap();
var proxyToSubject = /* @__PURE__ */ new WeakMap();
var observerToTriggers = /* @__PURE__ */ new WeakMap();
var defaultObservableOptions = { shallow: false };
var currentObserver = null;
function observer(f) {
	currentObserver = f;
	safeCallback(f);
	currentObserver = null;
	return () => unobserve(f);
}
function unobserve(observer) {
	cancelTask(observer);
	const triggers = observerToTriggers.get(observer);
	if (triggers) for (const [raw] of triggers.entries()) rawToObservers.get(raw)?.delete(observer);
	triggers && observerToTriggers.delete(observer);
}
function addObserver(raw) {
	if (!currentObserver) return;
	const observers = rawToObservers.get(raw);
	if (!observers) rawToObservers.set(raw, /* @__PURE__ */ new Set([currentObserver]));
	else if (!observers.has(currentObserver)) observers.add(currentObserver);
}
function addTrigger(raw, p) {
	if (!currentObserver) return;
	const triggers = observerToTriggers.get(currentObserver);
	if (triggers) {
		const trigger = triggers.get(raw);
		if (!trigger) triggers.set(raw, /* @__PURE__ */ new Set([p]));
		else if (!trigger.has(p)) trigger.add(p);
	} else observerToTriggers.set(currentObserver, /* @__PURE__ */ new Map([[raw, /* @__PURE__ */ new Set([p])]]));
}
var exclude = (value) => value instanceof Node || value instanceof Map || value instanceof Set || value instanceof WeakMap || value instanceof WeakSet || value instanceof RegExp || value instanceof Date || value instanceof Promise || (isArray(value) || isObject(value)) && Object.isFrozen(value);
function observable(raw, options = {}) {
	const { shallow } = Object.assign({}, defaultObservableOptions, options);
	const proxy = new Proxy(raw, {
		get(target, p, receiver) {
			const value = Reflect.get(target, p, receiver);
			if (exclude(value)) return value;
			addObserver(raw);
			addTrigger(raw, p);
			if (!shallow && (isObject(value) || isArray(value)) && !proxyToRaw.has(value)) return rawToProxy.has(value) ? rawToProxy.get(value) : observable(value, options);
			return value;
		},
		set(target, p, value, receiver) {
			const oldValue = Reflect.get(target, p, receiver);
			const res = Reflect.set(target, p, value, receiver);
			if (!isArray(target) && oldValue !== value ? true : p === "length") {
				effect(target, p);
				watchEffect(target, p);
			}
			return res;
		},
		deleteProperty(target, p) {
			const res = Reflect.deleteProperty(target, p);
			effect(target, p);
			watchEffect(target, p);
			return res;
		}
	});
	rawToProxy.set(raw, proxy);
	proxyToRaw.set(proxy, raw);
	addHmrObservable(proxy);
	return proxy;
}
function watch(proxy) {
	return (proxyToSubject.get(proxy) ?? proxyToSubject.set(proxy, createSubject()).get(proxy)).asReadonly();
}
//#endregion
export { observable, observer, observerToTriggers, proxyToSubject, rawToObservers, rawToProxy, unobserve, watch };
